/**
 * `matchMedia` as a hook — one implementation, because a second one drifts.
 *
 * Written for `KLQ-20260910-001` §B: the filter panel is a popover on a pointer and a `Drawer`
 * under 900px, and 900px is `components/screen`'s own breakpoint — the width at which the
 * frame stops pinning regions and lets the page scroll as one piece. A popover anchored to a
 * control in a stacked layout is a drawer everywhere else in this product.
 *
 * **Not a `ResizeObserver`.** `TreeTable` measures its own container, correctly, because *its*
 * question is "how much room have I been given". This one is about the device the page is on,
 * which is a viewport question and has a media query for it.
 *
 * `useSyncExternalStore` rather than `useState` + an effect, so the first render already knows
 * the answer: a filter bar that renders the popover branch and swaps to the drawer one frame
 * later is a flash on every phone.
 */

import { useCallback, useSyncExternalStore } from "react";

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => {
        list.removeEventListener("change", onChange);
      };
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    // Server snapshot. There is no SSR here, but the third argument is what stops a future
    // one from throwing on `window`; the wide branch is the safe default.
    () => false,
  );
}

/**
 * The breakpoint at which `components/screen` gives up and lets the page scroll.
 *
 * TWIN: the same 900px in `components/screen/Screen.module.css`, in
 * `components/slicer/Slicer.module.css` and in `features/tasks/Tasks.module.css`. A change
 * here is a change in all of them, in the same commit. (It used to name the drawings and
 * reviews stylesheets too; both dropped their own query when `Screen` took the layout over,
 * and the pointer went on naming them — retro rule 12's failure mode, found 2026-09-21.)
 */
export const STACKED = "(max-width: 900px)";

/**
 * The band where a list stops being a table and becomes a stack of cards.
 *
 * TWIN of `CARDS_MAX_WIDTH` in `components/tree-table/TreeTable.tsx` — change one and change
 * the other. It is **not** the same measurement, deliberately: the table asks a container
 * question (`ResizeObserver` on its own box, because a table in a split pane is as narrow as
 * one on a phone), and this asks a viewport question. `components/slicer` is the caller that
 * needs the viewport form: under this width the rail collapses into one chip that opens a
 * `Drawer`, and "is there room beside the list for a rail" is a question about the device, not
 * about the box the rail happens to be in.
 */
export const CARDS = "(max-width: 600px)";
